import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import { GENERIC_FILTER } from "shared/filterDefinitions";

import { UseSchema } from "./types";
import {
  createMinimalSchema,
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
  sortAndFilterSchemaByWhitelistedAccessors,
} from "./utils/schema-utils";
import useVMRSSchema from "./vmrsSchema";

const V2_VALUES_ENDPOINT = "v2/workOrders/repairParts";
const V2_REPAIRS_VALUES_ENDPOINT = "v2/workOrders/repairs";

const useRepairPartsSchema = (
  accessorsToHide: string[] = [],
  allowedRepairAccessors: string[] = []
): UseSchema => {
  const { attributes } = useAttributesContext(ContextProviderName.RepairPartV2);

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const getFilter = getFilterFunction(
    GENERIC_FILTER,
    attributeMap,
    V2_VALUES_ENDPOINT
  );

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const vmrsSchema = useVMRSSchema(["ID"]);

  // Create a minimal repairs schema inline to avoid circular dependency
  const { attributes: repairAttributes } = useAttributesContext(
    ContextProviderName.RepairV2
  );

  const repairsSchemaForRepairParts = createMinimalSchema(
    repairAttributes,
    GENERIC_FILTER,
    undefined,
    undefined,
    V2_REPAIRS_VALUES_ENDPOINT
  );

  // Apply filtering by allowed accessors if specified
  repairsSchemaForRepairParts.schema =
    sortAndFilterSchemaByWhitelistedAccessors(
      repairsSchemaForRepairParts.schema,
      allowedRepairAccessors
    );

  const relationships = {
    "/api/v2/vmrs": vmrsSchema,
    "/api/v2/workOrders/repairs": repairsSchemaForRepairParts,
  };

  const schema = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    undefined,
    relationships,
    accessorsToHide
  );

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  return {
    schema,
    attributes,
    getDisplayLabel,
    attributeMap,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry,
    getSchemaForAccessor,
  };
};

export default useRepairPartsSchema;
